// Whether this account is in at all.
//
// ADR-0023. The gate is post-login: by the time this is asked, the person has a
// session, and the only question left is whether the product is open to them.
//
// Nothing here is enforcement. A waitlisted account has no workspace, so every
// RLS policy in the database already denies it — this only decides which screen
// to show, and where the ring goes while the server thinks about it.

import { supabase } from "./supabase";

export type AccessState = "allowed" | "waitlisted" | "anonymous";

export interface Access {
  state: AccessState;
  /** member | invitation | allowlist | open — which door opened. */
  via: string | null;
  /** none | pending | waiting | invited — this account's own row, nobody else's. */
  waitlistStatus: string | null;
  /** Derived server-side, and only for a confirmed entry that is waiting. */
  waitlistPosition: number | null;
}

/** Before the answer arrives, assume nothing: `anonymous` shows no product. */
export const UNKNOWN_ACCESS: Access = {
  state: "anonymous", via: null, waitlistStatus: null, waitlistPosition: null,
};

export async function loadAccess(): Promise<Access> {
  const { data, error } = await supabase.rpc("my_access_state");
  if (error) throw error;
  const row = (Array.isArray(data) ? data[0] : data) as Record<string, unknown> | undefined;
  return {
    state: (row?.state as AccessState) ?? "anonymous",
    via: (row?.via as string) ?? null,
    waitlistStatus: (row?.waitlist_status as string) ?? null,
    waitlistPosition: (row?.waitlist_position as number) ?? null,
  };
}

/**
 * True when the code admitted them.
 *
 * Wrong, expired and revoked all come back the same way, on purpose: a field
 * that distinguishes them is a field for guessing codes against.
 */
export async function redeemJoinCode(code: string): Promise<boolean> {
  const { data, error } = await supabase.rpc("redeem_join_code", { p_code: code.trim() });
  if (error) return false;
  return data === true;
}

/**
 * The anonymous submission. It talks to the Worker, never to the database:
 * the `waitlist` schema is not exposed to PostgREST at all, so `supabase` has
 * no route to it from here even if someone tried.
 */
export async function joinWaitlist(input: {
  email: string;
  token: string | undefined;
  source?: string | null;
  campaign?: string | null;
}): Promise<void> {
  const base = import.meta.env.VITE_WAITLIST_WORKER_URL;
  if (!base) throw new Error("VITE_WAITLIST_WORKER_URL is not set");
  await fetch(`${base}/waitlist`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      email: input.email.trim().toLowerCase(),
      token: input.token,
      source: input.source ?? null,
      campaign: input.campaign ?? null,
      company: "",              // honeypot; a real person leaves it empty
    }),
  });
  // Deliberately no return value. The endpoint answers the same way for a new
  // address, a duplicate and an admitted one, and branching the UI on the
  // response would rebuild the enumeration oracle it was designed to remove.
}
